/**
 * crosshairSyncStore — cross-pane crosshair mirroring that costs ZERO React renders.
 *
 * WHY THIS EXISTS
 *
 * In 2-split and quad, ChartsPage passed `onCrosshairMove={setSyncCrosshair}` to every pane
 * and fed the resulting state back down as `syncCrosshair`. Single layout passed neither.
 * That one difference is why "I am unable to drag the charts smoothly" was reported for
 * split and quad and not for single: every crosshair event — and a pan-drag fires one per
 * mouse move — set state on an 8,900-line component, so the whole page re-rendered at
 * pointer rate. TradingChart's master data effect then re-ran (see the `auxCandles` memo in
 * ChartsPage, which used to hand it a fresh object identity on every render) and tore down
 * and rebuilt every overlay series, mid-drag.
 *
 * MEASURED on the live app, 2026-09-10, split 8H/15m, before this file existed:
 *   pane 0 drag: median frame 100ms, p95 1017ms, worst 1650ms, 79 long tasks (worst 1588ms)
 *   pane 1 drag: median frame  83ms, p95 1067ms, worst 1217ms, 73 long tasks (worst 2481ms)
 * A median frame of 83–100ms is 10–12fps. That is the complaint, in numbers.
 *
 * The receiving side never needed React at all: it only calls
 * `chart.setCrosshairPosition(...)`, which is imperative. So the sync is a plain module-level
 * pub/sub — publishers push, subscribers apply straight to their chart — and no component
 * anywhere re-renders because a pointer moved.
 *
 * A publisher never receives its own event: echoing the position back onto the pane the
 * pointer is actually over snaps the crosshair to the nearest bar centre instead of letting
 * it track the mouse freely.
 *
 * Subscriptions are keyed by GROUP so that a layout's panes mirror each other and nothing
 * else. The group is the layout mode, so switching layouts cannot leave a pane listening to
 * a chart that is no longer on screen.
 */

export interface CrosshairPos {
  /** Bar time (IST-shifted epoch seconds), or null when the pointer left the chart. */
  time: number | null;
  /** Price under the pointer, so the partner can mirror the horizontal line exactly. */
  price: number | null;
}

type Member = { id: number; apply: (pos: CrosshairPos) => void };

const groups = new Map<string, Set<Member>>();
let nextId = 1;

export interface CrosshairHandle {
  /** Broadcast this pane's crosshair to every OTHER pane in the group. */
  publish: (pos: CrosshairPos) => void;
  /** Remove this pane from the group. Safe to call twice. */
  leave: () => void;
}

/**
 * Join a mirroring group.
 *
 * @param group  Panes sharing this key mirror each other. Pass '' / undefined to opt out
 *               entirely — a single-pane layout has nothing to mirror and must never touch
 *               its own crosshair.
 * @param apply  Called with a position published by ANOTHER pane in the group. Do the work
 *               imperatively; this is deliberately not a React state setter.
 */
export function joinCrosshairGroup(
  group: string | undefined,
  apply: (pos: CrosshairPos) => void,
): CrosshairHandle | null {
  if (!group) return null;
  const me: Member = { id: nextId++, apply };
  let set = groups.get(group);
  if (!set) { set = new Set(); groups.set(group, set); }
  set.add(me);
  let joined = true;
  return {
    publish(pos: CrosshairPos) {
      if (!joined) return;
      const members = groups.get(group);
      if (!members) return;
      members.forEach(m => {
        if (m.id === me.id) return;               // never echo back to the publisher
        try { m.apply(pos); } catch { /* one bad pane must not stop the rest */ }
      });
    },
    leave() {
      if (!joined) return;
      joined = false;
      const members = groups.get(group);
      if (!members) return;
      members.delete(me);
      if (members.size === 0) groups.delete(group);
    },
  };
}

/** Test/debug only: how many panes are currently mirroring in a group. */
export function crosshairGroupSize(group: string): number {
  return groups.get(group)?.size ?? 0;
}
